import "./styles.scss";

import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import classnames from "classnames";

import React from "react";
import { useApp } from "context/app";
import { useNotifications } from "context/notifications";

import NotificationsActions from "../Actions";
import SelectablePill from "components/SelectablePill";
import Button from "components/Button";

import useResponsive from "libs/helpers/useResponsive";
import notificationRender, {
  getNotificationTitle,
  isNewTaskNotification,
} from "libs/notification_render";

import { ReactComponent as CheckIcon } from "assets/icons/new/check.svg";
import { ReactComponent as TwoChecksIcon } from "assets/icons/new/two-checks.svg";
import { ReactComponent as NoDataIcon } from "assets/icons/no-data.svg";

const mainClass = "notifications-tiles";

const NotificationsTiles = ({ setWebsiteInfoId }) => {
  const { t } = useTranslation();
  const { max } = useResponsive();

  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => {
    if (max.lg) {
      const params = new URLSearchParams(location.search);

      if (params.has("sort")) {
        params.delete("sort");
        navigate({ search: params.toString() }, { replace: true });
      }
    }
  }, [location, navigate, max.lg]);

  const { user } = useApp();
  const { data, readHandler, show_read, setShowRead } = useNotifications();

  const unreaded_count = data?.reduce((a, b) => a + (!!!b?.status ? 1 : 0), 0);

  return (
    <div className={mainClass}>
      <div className={`${mainClass}__wrapper`}>
        <div className={`${mainClass}__header`}>
          {max.sm ? (
            <>
              <ul className={`${mainClass}__sort`}>
                <li>
                  <button
                    className={classnames({
                      active: !show_read,
                    })}
                    type="button"
                    onClick={() => setShowRead(false)}
                  >
                    {t("New [NOTIFICATIONS]")}
                  </button>
                </li>
                <li>
                  <button
                    className={classnames({
                      active: show_read,
                    })}
                    type="button"
                    onClick={() => setShowRead(true)}
                  >
                    {t("Read")}
                  </button>
                </li>
              </ul>
              {unreaded_count > 0 && (
                <div className={`${mainClass}__read-all`}>
                  <button type="button" onClick={() => readHandler("all")}>
                    <i>
                      <TwoChecksIcon />
                    </i>
                    <span>{t("Mark all as read")}</span>
                  </button>
                </div>
              )}
            </>
          ) : (
            <NotificationsActions />
          )}
        </div>
        <div className={`${mainClass}__row`}>
          {data?.length > 0 ? (
            data.map(
              ({
                _id,
                template,
                created_at,
                website,
                status,
                custom_text,
                context,
                href,
                task,
              }) => {
                const taskAddedToUser = template === 21;
                const newWebsiteComment = template === null || template === 24;
                const message = isNewTaskNotification(template)
                  ? task?.description
                  : custom_text ||
                    notificationRender(template, context, t)?.text;
                const type =
                  template === 17 ||
                  template === 18 ||
                  template === 19 ||
                  template === 20 ||
                  taskAddedToUser
                    ? "task"
                    : template === 28
                      ? "register"
                      : "website";

                return (
                  <div
                    key={_id}
                    className={classnames(`${mainClass}__item`, {
                      [`${mainClass}__item--read`]: status === 1,
                    })}
                  >
                    <div className={`${mainClass}__item-header`}>
                      <p>{moment(created_at)?.format("LL")}</p>
                      {website?.website && (
                        <button
                          type="button"
                          onClick={() =>
                            user?.type === "client"
                              ? navigate(`/websites/${website?._id}`)
                              : setWebsiteInfoId(website?._id)
                          }
                        >
                          {website.website}
                        </button>
                      )}
                    </div>
                    <h6>
                      {getNotificationTitle({ template, context, task }, t)}
                    </h6>
                    {message && (
                      <p dangerouslySetInnerHTML={{ __html: message }} />
                    )}
                    <div className={`${mainClass}__item-footer`}>
                      <SelectablePill
                        data={{
                          label: t(
                            template === 17 ||
                              template === 18 ||
                              template === 19 ||
                              template === 20 ||
                              taskAddedToUser
                              ? "Task"
                              : template === 28
                                ? "Registration"
                                : "Website [SHORT]",
                          ),
                          color:
                            template === 17 ||
                            template === 18 ||
                            template === 19 ||
                            template === 20 ||
                            taskAddedToUser
                              ? "yellow"
                              : template === 28
                                ? "blue"
                                : "green",
                        }}
                      />
                      <button
                        className={`${mainClass}__item__read`}
                        onClick={() => readHandler(_id, status === 1 ? 0 : 1)}
                      >
                        <i>
                          <CheckIcon />
                        </i>
                        <span>
                          {t(`Mark as ${status !== 1 ? "read" : "unread"}`)}
                        </span>
                      </button>
                    </div>
                    {(newWebsiteComment
                      ? website?._id
                      : href?.indexOf("undefined") === -1) && (
                      <div className={`${mainClass}__item-cta`}>
                        <Button
                          href={
                            newWebsiteComment
                              ? user?.type === "client"
                                ? website?._id
                                  ? `/websites/${website?._id}`
                                  : undefined
                                : undefined
                              : taskAddedToUser
                                ? href.replace("websites", "tasks")
                                : href
                          }
                          onClick={() => {
                            readHandler(_id, 1);

                            if (newWebsiteComment && user?.type !== "client") {
                              if (typeof setWebsiteInfoId === "function")
                                setWebsiteInfoId(website?._id);
                            }
                          }}
                        >
                          {t(
                            `Go to ${type === "task" ? "task" : type === "register" ? "registration" : "website"}`,
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              },
            )
          ) : (
            <div className={`${mainClass}__no-data`}>
              <NoDataIcon />
              <p>{t("No data")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsTiles;
